/**
 * Landed cost — twelve lines, three readings, and one word doing most of the work.
 *
 * ── The word is "assumed" ────────────────────────────────────────────────────
 *
 * Every row in the seeded assumption library ships with `verified_on = NULL`.
 * That is not a blank; it means NOBODY HAS EVER CHECKED THIS. §7.4 costs it
 * five confidence points a key, capped at thirty, which is exactly why a fresh
 * install lands at INDICATIVE and cannot claim FIRM — §7.7's worked example
 * scores 56 for this reason and rises the moment the owner verifies the barge
 * and tow rates.
 *
 * So the band leads the panel, and every line still standing on an unverified
 * assumption carries the word **assumed** next to its number. A band at the
 * top of a panel is not enough: the line is what somebody reads out to a
 * hauler, and a line that looks firm because the header scrolled off is the
 * failure this is written to prevent.
 *
 * ── The three readings are not interchangeable ───────────────────────────────
 *
 * `low` is "if everything goes right", `high` is "if everything goes wrong",
 * `expected` is "plan against this" — and `expected` is the figure every
 * threshold, alert and Next Best Action rule compares against, so it is the
 * largest and boldest thing on the panel. Summing twelve independent lows
 * produces a floor that will never actually happen; §7.4 accepts that and
 * LABELS it rather than narrowing the range into something nobody can
 * reproduce by adding up the visible column.
 *
 * ── §7 ───────────────────────────────────────────────────────────────────────
 */

import { useCallback } from 'react'
import { useLocale, type Locale } from '../../lib/i18n'
import {
  assumed,
  costBandLabel,
  costBandTone,
  delta,
  money,
  percentFromBps,
  range,
  readingHint,
  readingLabel,
  TONES,
} from '../../lib/format'
import { COST_CATEGORIES, costBasisLabel, costCategoryLabel, humanise } from '../../lib/domain'
import { Chip, Empty, Line, Panel, S } from './ui'
import type { CostLine, CostSummary, NegotiationView } from './types'

const EN = {
  title: 'Landed cost',
  unavailable: 'The landed cost could not be loaded.',
  none: 'Nothing has been costed yet.',
  noneWhy: 'A landed cost appears once there is an asking price to build it on.',
  confidence: 'Cost confidence',
  whySpread: 'Spread',
  whyStale: 'Unverified assumptions',
  whyUnknown: 'Missing inputs',
  unverifiedHeading: 'Numbers nobody has checked',
  unverifiedBody:
    'These assumptions ship as placeholders. They are usable and they are honestly labelled — verifying one records the value, the source and today’s date, and the estimate firms up straight away.',
  missingHeading: 'Not known yet',
  actual: 'Actual',
  estimate: 'Estimate',
  variance: 'Variance',
  backed: 'backed by a receipt',
  locked: 'set by hand — a recompute will not touch it',
  negotiation: 'What to offer',
  negotiationNone: 'No suggestion yet',
  asking: 'Asking',
  original: 'First price we saw',
  reductions: 'Price reductions',
  daysListed: 'Days listed',
  clamped: 'The clamp decided one end of this, not the formula.',
  advisory: 'Advisory only. Nothing here sends anything to anybody.',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Costo puesto en el terreno',
  unavailable: 'No se pudo cargar el costo.',
  none: 'Todavía no se ha calculado un costo.',
  noneWhy: 'El costo aparece cuando hay un precio pedido sobre el cual construirlo.',
  confidence: 'Confianza del costo',
  whySpread: 'Rango',
  whyStale: 'Supuestos sin verificar',
  whyUnknown: 'Datos que faltan',
  unverifiedHeading: 'Números que nadie ha comprobado',
  unverifiedBody:
    'Estos supuestos vienen como valores provisionales. Se pueden usar y están marcados con honestidad — al verificar uno se guarda el valor, la fuente y la fecha de hoy, y el estimado se afirma enseguida.',
  missingHeading: 'Todavía sin saber',
  actual: 'Real',
  estimate: 'Estimado',
  variance: 'Diferencia',
  backed: 'respaldado por un recibo',
  locked: 'puesto a mano — un recálculo no lo toca',
  negotiation: 'Qué ofrecer',
  negotiationNone: 'Todavía no hay sugerencia',
  asking: 'Piden',
  original: 'Primer precio que vimos',
  reductions: 'Bajadas de precio',
  daysListed: 'Días publicado',
  clamped: 'El tope decidió uno de los extremos, no la fórmula.',
  advisory: 'Solo orientación. Nada de esto le manda nada a nadie.',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/**
 * Is this line still standing on a placeholder?
 *
 * `basis = 'assumption'` is the only basis a recompute may overwrite — the
 * others carry `locked = 1` in SQL. So an assumption-based line is exactly the
 * set of lines that are somebody's best guess rather than somebody's number,
 * and those are the ones that wear the word.
 */
const isAssumed = (line: CostLine): boolean => line.basis === 'assumption'

export default function CostBreakdown({ cost, failed = false }: { cost: CostSummary | null; failed?: boolean }) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const band = cost?.confidence.band ?? 'ROUGH'
  const bandTone = costBandTone(band)
  // The server already returns them in category order; sorting here means a
  // line for a category this build has not heard of still lands at the end
  // rather than wherever the database happened to put it.
  const rank = new Map<string, number>(COST_CATEGORIES.map((c, i) => [c, i]))
  const lines = [...(cost?.lines ?? [])].sort((a, b) => (rank.get(a.category) ?? 99) - (rank.get(b.category) ?? 99))
  const anyAssumed = lines.some(isAssumed)

  return (
    <Panel
      id="cost"
      title={t('title')}
      defaultOpen
      badge={
        cost ? (
          <Chip tone={bandTone} title={`${cost.confidence.score}/100`}>
            {costBandLabel(band, locale)}
          </Chip>
        ) : undefined
      }
    >
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : !cost || lines.length === 0 ? (
        <>
          <Empty>{t('none')}</Empty>
          <p style={S.note}>{t('noneWhy')}</p>
        </>
      ) : (
        <>
          {/* Expected first, largest, boldest. It is the figure every rule
              compares against, so it is the figure the eye lands on. */}
          <div style={{ marginTop: 4 }}>
            <div style={{ ...S.huge, color: 'var(--money)' }}>
              {money(cost.total.expected, locale)}
              {anyAssumed && (
                <span style={{ marginLeft: 8, verticalAlign: 'middle' }}>
                  <Chip tone={TONES.quiet} outlined>
                    {assumed(locale)}
                  </Chip>
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{readingHint('expected', locale)}</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 8 }}>{range(cost.total, locale)}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              {readingLabel('low', locale)}: {readingHint('low', locale)} · {readingLabel('high', locale)}:{' '}
              {readingHint('high', locale)}
            </div>
          </div>

          {/* Cost confidence, with its working. A band nobody can take apart is
              a band nobody believes. */}
          <div style={{ marginTop: 14 }}>
            <Line label={t('confidence')} first>
              {cost.confidence.score}/100 <Chip tone={bandTone}>{costBandLabel(band, locale)}</Chip>
            </Line>
            <Line label={t('whySpread')}>−{cost.confidence.spreadPenalty}</Line>
            <Line label={t('whyStale')}>−{cost.confidence.stalePenalty}</Line>
            <Line label={t('whyUnknown')}>−{cost.confidence.unknownPenalty}</Line>
            {cost.actualCoverageBps > 0 && (
              <Line label={t('actual')}>
                {money(cost.actualCents, locale)}{' '}
                <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>
                  ({percentFromBps(cost.actualCoverageBps, locale)} {t('backed')})
                </span>
              </Line>
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            {lines.map((line, index) => (
              <CostRow key={line.id} line={line} first={index === 0} locale={locale} t={t} />
            ))}
          </div>

          {/* Named, not counted. "Thirty points of stale penalty" is not
              actionable; "the barge rate and the tow rate" is. */}
          {cost.confidence.staleKeys.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 4px' }}>{t('unverifiedHeading')}</h3>
              <p style={S.note}>{t('unverifiedBody')}</p>
              <div style={{ ...S.chipRow, marginTop: 8 }}>
                {cost.confidence.staleKeys.map((key) => (
                  <Chip key={key} tone={TONES.quiet} outlined title={key}>
                    {key}
                  </Chip>
                ))}
              </div>
            </div>
          )}

          {cost.confidence.missing.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 4px' }}>{t('missingHeading')}</h3>
              <div style={S.chipRow}>
                {cost.confidence.missing.map((key) => (
                  <Chip key={key} tone={TONES.warn}>
                    {humanise(key)}
                  </Chip>
                ))}
              </div>
            </div>
          )}

          <Negotiation view={cost.negotiation} locale={locale} t={t} />
        </>
      )}
    </Panel>
  )
}

function CostRow({
  line,
  first,
  locale,
  t,
}: {
  line: CostLine
  first: boolean
  locale: Locale
  t: (key: CopyKey) => string
}) {
  const variance = line.actual_cents === null ? null : line.actual_cents - line.expected_cents

  return (
    <div style={{ padding: '10px 0', borderTop: first ? 'none' : '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{costCategoryLabel(line.category, locale)}</span>
        <span style={{ fontSize: 15, fontWeight: 700 }}>{money(line.expected_cents, locale, { showCents: true })}</span>
      </div>

      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
        {money(line.low_cents, locale, { showCents: true })} – {money(line.high_cents, locale, { showCents: true })}
      </div>

      <div style={{ ...S.chipRow, marginTop: 6 }}>
        <Chip tone={line.basis === 'quote' ? TONES.strong : TONES.quiet} outlined={line.basis === 'assumption'}>
          {costBasisLabel(line.basis, locale)}
        </Chip>
        {isAssumed(line) && (
          <Chip tone={TONES.quiet} outlined title={t('unverifiedBody')}>
            {assumed(locale)}
          </Chip>
        )}
        {line.locked === 1 && (
          <Chip tone={TONES.brand} title={t('locked')}>
            {t('locked')}
          </Chip>
        )}
      </div>

      {/* Every road line's explanation says "estimated from straight-line
          distance × 1.30; not a routed distance" — §7.2 requires it, and it is
          rendered exactly as written rather than summarised. */}
      {line.explanation && <p style={S.note}>{line.explanation}</p>}

      {line.actual_cents !== null && (
        <div style={{ marginTop: 6 }}>
          <Line label={t('actual')} first>
            {money(line.actual_cents, locale, { showCents: true })}
          </Line>
          <Line label={t('variance')}>
            <span style={{ color: (variance ?? 0) > 0 ? 'var(--danger)' : 'var(--score-strong)' }}>
              {delta(variance, locale, { showCents: true })}
            </span>
          </Line>
          {line.actual_note && <p style={S.note}>{line.actual_note}</p>}
        </div>
      )}
    </div>
  )
}

/**
 * §7.5's advisory range — or its refusal, which is a real answer.
 *
 * With no prior reductions, no known original price and fewer than seven days
 * listed there is no number, and the sentence says so. A suggested range with
 * nothing behind it is a number the owner will quote to a seller, and being
 * wrong there costs real money.
 */
function Negotiation({
  view,
  locale,
  t,
}: {
  view: NegotiationView | null
  locale: Locale
  t: (key: CopyKey) => string
}) {
  if (!view) return null
  const { outcome, context } = view

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 6px' }}>{t('negotiation')}</h3>

      {outcome.ok ? (
        <>
          <div style={{ ...S.big, color: 'var(--money)' }}>
            {money(outcome.lowCents, locale)} – {money(outcome.highCents, locale)}
          </div>
          <div style={{ marginTop: 8 }}>
            {outcome.terms.map((term, index) => (
              <div key={`${term.code}-${index}`} style={index === 0 ? S.rowFirst : S.row}>
                <span style={S.label}>{term.message}</span>
                <span style={{ ...S.value, fontSize: 13 }}>{percentFromBps(term.bps, locale)}</span>
              </div>
            ))}
          </div>
          {outcome.clamped && <p style={S.note}>{t('clamped')}</p>}
        </>
      ) : (
        <>
          <p style={{ ...S.sentence, fontWeight: 600 }}>{outcome.message}</p>
          <p style={S.note}>{t('negotiationNone')}</p>
        </>
      )}

      <div style={{ marginTop: 10 }}>
        <Line label={t('asking')} first>
          {money(context.asking, locale)}
        </Line>
        {context.original !== null && <Line label={t('original')}>{money(context.original, locale)}</Line>}
        <Line label={t('reductions')}>{context.priorReductions}</Line>
        <Line label={t('daysListed')}>{context.daysListed}</Line>
      </div>

      <p style={S.note}>{t('advisory')}</p>
    </div>
  )
}
